import type { ReactNode } from "react";
import { AHEAD_PRESETS, COMPARE_PRESETS, MAX_AHEAD_DAYS, NOTHING_TO_LOOK_BACK_ON, daysAgo, daysAhead, toInputDate, type CompareDirection } from "./compareModel";

/**
 * Where Then is chosen, under the search row while compare mode is on: presets, an exact day, and a slot on
 * the right that the timeline scrubber will fill. Loading and failing are said HERE, beside the control that
 * caused them, while the Now dial and the card stay usable.
 */
export function RangeBar({ day, status, onDay, onRetry, scrubber, empty = false, direction = "back" }: {
  /** KEY-890: Look ahead picks a later day — its own pills, and the date field runs forward from today. */
  direction?: CompareDirection;
  day: string;
  status: "idle" | "loading" | "ready" | "error";
  onDay: (day: string) => void;
  onRetry: () => void;
  /** The timeline scrubber (wide screens only). Without it the slot says what's loading, or nothing. */
  scrubber?: ReactNode;
  /**
   * KEY-760: the dial has no history yet. The bar says so in its slot and its controls are disabled, so there
   * is no past to pick and nothing to fail — rather than offering three months back on a dial made today.
   */
  empty?: boolean;
}) {
  const today = toInputDate(new Date());
  const ahead = direction === "ahead";
  return (
    <div className="kd-rangebar" data-status={empty ? "empty" : status} data-direction={direction} role="group" aria-label={ahead ? "Look ahead to" : "Look back to"}>
      <span className="kd-rangebar-label">{ahead ? "Ahead" : "Then"}</span>
      <div className="kd-rangebar-presets" role="group" aria-label="Jump to">
        {(ahead ? AHEAD_PRESETS : COMPARE_PRESETS).map((p) => {
          const value = ahead ? daysAhead(p.days) : daysAgo(p.days);
          return (
            <button
              key={p.id}
              type="button"
              className="kd-rangebar-pill kd-focusable"
              aria-pressed={!empty && day === value}
              disabled={empty}
              onClick={() => onDay(value)}
            >
              {p.label}
            </button>
          );
        })}
      </div>
      <input
        type="date"
        className="kd-rangebar-date kd-focusable"
        aria-label={ahead ? "Ahead date" : "Then date"}
        value={day}
        min={ahead ? daysAhead(1) : undefined}
        max={ahead ? daysAhead(MAX_AHEAD_DAYS) : today}
        disabled={empty}
        onChange={(e) => e.target.value && onDay(e.target.value)}
      />
      <div className="kd-rangebar-slot">
        {empty ? (
          <span className="kd-rangebar-note" role="status">{NOTHING_TO_LOOK_BACK_ON}</span>
        ) : status === "error" ? (
          <span className="kd-rangebar-note" role="alert">
            {ahead ? "Couldn’t look ahead just now." : "Couldn’t look back just now."}
            <button type="button" className="kd-rangebar-retry kd-focusable" onClick={onRetry}>
              Try again
            </button>
          </span>
        ) : scrubber ? (
          // The scrubber stays put while a replay loads — taking it away mid-drag would drop the pointer.
          // The spinner and the bar along the track say "loading" instead.
          <>
            {status === "loading" && (
              <span className="kd-rangebar-spinner" role="status" aria-label={ahead ? "Turning the clock forward…" : "Turning the clock back…"} />
            )}
            {scrubber}
          </>
        ) : status === "loading" ? (
          <span className="kd-rangebar-note" role="status">
            <span className="kd-rangebar-spinner" aria-hidden="true" />
            {ahead ? "Turning the clock forward…" : "Turning the clock back…"}
          </span>
        ) : null}
      </div>
    </div>
  );
}
